import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { api, fetchMe, type AppUser } from '../api/client';
import { buildReturnTo, getCurrentBrowserPath } from '../lib/auth/navigation';
import { isAuth0SessionError } from '../lib/auth/sessionErrors';
import { getAtuacao, HEADER_ATUANDO_POR, rotaDaPessoa, setAtuacao } from '../lib/delegacao/atuacao';
import { handleUnauthorizedResponse } from '../lib/auth/unauthorized';
import { identifyClarityUser } from '../lib/observability/clarity';
import { clearSentryUser, setSentryUser } from '../lib/observability/sentry';
import { clearOfflineData } from '../lib/pwa/clearOfflineData';
import { limparRascunhosDoUsuario } from '../lib/editor/rascunhoLocal';
import { clearErrorQueue } from '../services/errorTracking';

export interface WhitelistBlockedState {
  email: string;
  interestRegistered: boolean;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  emailVerified: boolean;
  authError: string | null;
  /** Título da tela de erro; `null` = falha de conexão (título padrão). */
  authErrorTitulo: string | null;
  user: AppUser | null;
  whitelistBlocked: WhitelistBlockedState | null;
  login: (selectedPlan?: string, returnTo?: string, screenHint?: 'login' | 'signup') => void;
  logout: () => void;
  getToken: () => Promise<string>;
  /**
   * Força renovação do token ignorando o cache do SDK. Usado pelo SSE
   * (`useProcessualEvents`) ao receber 401 no `onopen` — fetch-event-source
   * não passa pelo interceptor Axios, então precisamos pedir um token novo
   * explicitamente. Retorna null se a renovação falhar (sessão precisa de
   * reautenticação).
   */
  refreshTokenSilently: () => Promise<string | null>;
  refreshUser: () => Promise<void>;
  markInterestRegistered: () => void;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function AuthProviderInner({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authErrorTitulo, setAuthErrorTitulo] = useState<string | null>(null);
  const [whitelistBlocked, setWhitelistBlocked] = useState<WhitelistBlockedState | null>(null);

  // Cache do token para evitar chamar getAccessTokenSilently() em toda request.
  // O SDK Auth0 já faz cache interno, mas a chamada assíncrona tem overhead (~50-100ms).
  // Com cache local, requests paralelas reutilizam o mesmo token instantaneamente.
  const tokenRef = useRef<string | null>(null);
  const reauthInProgressRef = useRef(false);
  const locationRef = useRef(location);

  // Evita reinstalar interceptors a cada navegação só para capturar a rota atual.
  locationRef.current = location;

  const refreshToken = useCallback(async () => {
    const token = await getAccessTokenSilently();
    tokenRef.current = token;
    return token;
  }, [getAccessTokenSilently]);

  const beginReauthentication = useCallback(() => {
    if (reauthInProgressRef.current) {
      return;
    }

    reauthInProgressRef.current = true;
    tokenRef.current = null;

    // Só persistir rotas protegidas. Salvar rotas públicas (ex.: "/")
    // faz o callback pós-login navegar de volta para a landing em vez do app.
    const returnTo = buildReturnTo(locationRef.current);
    if (returnTo.startsWith('/app')) {
      sessionStorage.setItem('jurisapp:returnTo', returnTo);
    }

    setUser(null);
    setAuthError(null);
    setIsLoadingUser(false);

  }, []);

  // Interceptor Axios: usa token cacheado, sem await em toda request
  useEffect(() => {
    const interceptor = api.interceptors.request.use(async (config) => {
      // Contexto delegado decidido UMA vez por requisição, no instante em que ela é
      // criada e ANTES de qualquer await (fail-closed — ver lib/delegacao/atuacao).
      // O retry do 401 passa por aqui de novo com o mesmo config: reusa a decisão,
      // nunca relê o contexto (que pode ter mudado durante o refresh do token).
      if (config.atuandoPor === undefined) {
        const atuacao = getAtuacao();
        config.atuandoPor =
          atuacao && !config.semAtuacao && !rotaDaPessoa(config.url ?? '') ? atuacao.titularId : null;
      }
      if (config.atuandoPor) {
        config.headers[HEADER_ATUANDO_POR] = config.atuandoPor;
      } else {
        delete config.headers[HEADER_ATUANDO_POR];
      }
      try {
        if (!tokenRef.current) {
          tokenRef.current = await getAccessTokenSilently();
        }
        config.headers.Authorization = `Bearer ${tokenRef.current}`;
      } catch {
        // Sem token disponível (sessão expirada ou usuário deslogado).
        // Request segue sem Authorization — o response interceptor
        // trata o 401 com retry de token.
        tokenRef.current = null;
      }
      return config;
    });

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => handleUnauthorizedResponse({
        error,
        refreshToken: async () => {
          tokenRef.current = null;
          return refreshToken();
        },
        retryRequest: (request) => api(request),
        onUnauthorized: beginReauthentication,
      }),
    );

    return () => {
      api.interceptors.request.eject(interceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [beginReauthentication, getAccessTokenSilently, refreshToken]);

  // Carregar dados do usuário local quando autenticado no Auth0.
  // Dependências mínimas: só dispara quando auth0Loading muda para false
  // ou quando isAuthenticated/auth0User mudam de valor.
  useEffect(() => {
    if (auth0Loading) return;

    const loadUser = async () => {
      if (isAuthenticated && auth0User) {
        try {
          // Busca token uma vez e cacheia para as requests seguintes
          tokenRef.current = await getAccessTokenSilently();
          const me = await fetchMe(tokenRef.current);
          reauthInProgressRef.current = false;
          // Contexto delegado herdado da aba (sessionStorage) de OUTRO login: descarta.
          if (getAtuacao() && getAtuacao()?.atorId !== me.id) {
            setAtuacao(null);
          }
          setUser(me);
          setWhitelistBlocked(null);
          localStorage.setItem('jurisapp:userId', me.id);
          if (auth0User.sub) {
            localStorage.setItem('jurisapp:userSub', auth0User.sub);
          }
          // Identifica o usuário nas ferramentas de observabilidade externas.
          // Usamos me.id (UUID interno) — mesmo valor logado pelo backend em
          // structlog/CloudWatch, garantindo correlação 1:1 cross-tool.
          // Para o Clarity, também passamos o primeiro nome como friendly-name:
          // aparece no painel do Clarity como rótulo legível em vez do UUID.
          // Em prod, com base de usuários maior, considerar mascarar/encurtar.
          setSentryUser(me.id);
          identifyClarityUser(me.id, me.nome?.split(' ')[0] || undefined);
          setAuthError(null);
        } catch (err) {
          // 401 já tratado pelo interceptor (beginReauthentication + _authHandled)
          // Não exibir tela de erro — o redirect para /login já está em andamento
          if ((err as Record<string, unknown>)?._authHandled) {
            return;
          }

          // 403 whitelist_required: usuário autenticado no Auth0 mas não convidado
          // para o piloto. Renderizamos a BlockedPage em vez de erro genérico.
          const response = (err as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
          if (response?.status === 403 && response.data?.code === 'whitelist_required') {
            const payload = response.data as { email?: string; interest_registered?: boolean };
            setWhitelistBlocked({
              email: payload.email ?? auth0User.email ?? '',
              interestRegistered: Boolean(payload.interest_registered),
            });
            setUser(null);
            setAuthError(null);
            setIsLoadingUser(false);
            return;
          }

          if (isAuth0SessionError(err)) {
            // Sessão Auth0 expirada/inválida — forçar re-login
            beginReauthentication();
            return;
          }

          // Recusa do backend com motivo (ex.: 403 "Conta desativada"): mostrar o
          // motivo. Chamar isso de "erro ao conectar" mandava o usuário tentar de novo
          // algo que não vai mudar.
          const detalhe = typeof response?.data?.detail === 'string' ? response.data.detail : null;
          const status = response?.status ?? 0;
          setUser(null);
          if (status >= 400 && status < 500 && detalhe) {
            setAuthErrorTitulo(status === 403 ? 'Acesso não permitido' : 'Não foi possível entrar');
            setAuthError(detalhe);
          } else {
            setAuthErrorTitulo(null);
            setAuthError('Erro ao conectar ao servidor. Tente novamente em alguns instantes.');
          }
        }
      } else {
        setUser(null);
        setAuthError(null);
        // NÃO zerar whitelistBlocked aqui — o Auth0 SDK pode oscilar
        // (ex.: token refresh) e disparar este branch transitoriamente
        // mesmo com o bloqueio ativo. Limpar aqui faz o gate em App.tsx
        // liberar as <Routes>, e como user=null → emailVerified=false,
        // ProtectedRoute redireciona para /verificar-email com email vazio.
        // O bloqueio é zerado apenas no caminho de sucesso (após fetchMe ok)
        // ou no logout explícito (auth0Logout recarrega a página).
      }
      setIsLoadingUser(false);
    };

    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth0User, auth0Loading, beginReauthentication, getAccessTokenSilently, isAuthenticated]);

  const login = useCallback(
    (selectedPlan?: string, returnTo?: string, screenHint?: 'login' | 'signup') => {
      const currentPath = buildReturnTo(location);
      const fallbackReturnTo = currentPath.startsWith('/app') ? currentPath : '/app';

      loginWithRedirect({
        appState: {
          returnTo: returnTo || fallbackReturnTo,
          selectedPlan: selectedPlan || 'free_trial',
        },
        // screen_hint='signup' abre a Auth0 Universal Login direto na aba de cadastro.
        ...(screenHint ? { authorizationParams: { screen_hint: screenHint } } : {}),
      });
    },
    [loginWithRedirect, location],
  );

  const logout = useCallback(async () => {
    // Não limpar estado React antes do redirect — senão ProtectedRoute
    // redireciona para /login antes do Auth0 conseguir fazer o logout.
    // O auth0Logout faz window.location.assign(), descarregando a página.
    tokenRef.current = null;
    reauthInProgressRef.current = false;
    clearSentryUser();
    setAtuacao(null);

    // Limpar outbox do usuário para evitar envio de mutações após troca de conta.
    // Query cache e doc cache permanecem (já isolados por userId).
    // NÃO removemos jurisapp:userId/userSub — são necessários para reaproveitar
    // cache no relogin com backend indisponível. A segurança de identidade é
    // garantida pelo sub-matching no UserScopedPersistence.
    const uid = user?.id ?? localStorage.getItem('jurisapp:userId');
    if (uid) {
      await clearOfflineData(uid).catch(() => {});
      // Limpar fila de erros do front (memória + localStorage escopado): pode
      // conter PII e não deve ser reenviada sob o token do próximo usuário.
      clearErrorQueue(uid);
      // Texto do editor dos autos guardado no aparelho: é do processo, não fica
      // para o próximo que entrar nesta máquina.
      limparRascunhosDoUsuario(uid);
    }

    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [auth0Logout, user]);

  const getToken = useCallback(async () => {
    const token = await refreshToken();
    return token;
  }, [refreshToken]);

  const refreshTokenSilently = useCallback(async (): Promise<string | null> => {
    try {
      // Auth0 SDK v2: cacheMode: 'off'; v1 antigo: ignoreCache: true.
      // Passamos os dois para portabilidade entre versões.
      const token = await getAccessTokenSilently({
        cacheMode: 'off',
        ignoreCache: true,
      } as Parameters<typeof getAccessTokenSilently>[0]);
      tokenRef.current = token;
      return token;
    } catch {
      // Renovação falhou — caller dispara reauth/logout. Falha é esperada
      // (refresh token expirado, sessão revogada) e silenciada aqui.
      return null;
    }
  }, [getAccessTokenSilently]);

  const refreshUser = useCallback(async () => {
    if (!tokenRef.current) {
      tokenRef.current = await getAccessTokenSilently();
    }
    const me = await fetchMe(tokenRef.current);
    setUser(me);
  }, [getAccessTokenSilently]);

  const markInterestRegistered = useCallback(() => {
    setWhitelistBlocked((prev) => (prev ? { ...prev, interestRegistered: true } : prev));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: isAuthenticated && !!user,
      isLoading: auth0Loading || isLoadingUser,
      emailVerified: user?.email_verified ?? false,
      authError,
      authErrorTitulo,
      user,
      whitelistBlocked,
      login,
      logout,
      getToken,
      refreshTokenSilently,
      refreshUser,
      markInterestRegistered,
    }),
    [isAuthenticated, auth0Loading, isLoadingUser, authError, authErrorTitulo, user, whitelistBlocked, login, logout, getToken, refreshTokenSilently, refreshUser, markInterestRegistered]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: { returnTo?: string; selectedPlan?: string }) => {
    if (appState?.selectedPlan) {
      sessionStorage.setItem('jurisapp:selectedPlan', appState.selectedPlan);
    }

    // Verificar sessionStorage para rota salva por fluxos OAuth externos
    // (ex: Google Calendar) que causam reload completo da página.
    // Só aceitar se for rota protegida — evita resíduo de sessão anterior
    // levar o usuário de volta para a landing após o login.
    const rawSaved = sessionStorage.getItem('jurisapp:returnTo');
    if (rawSaved) {
      sessionStorage.removeItem('jurisapp:returnTo');
    }
    const savedReturn = rawSaved && rawSaved.startsWith('/app') ? rawSaved : null;

    const currentPath = getCurrentBrowserPath();
    const fallbackReturnTo = currentPath.startsWith('/app') ? currentPath : '/app';
    // appState.returnTo vem do login() recém-invocado e tem prioridade sobre
    // savedReturn, que é um fallback para fluxos OAuth externos sem appState.
    const target = appState?.returnTo || savedReturn || fallbackReturnTo;
    navigate(target.startsWith('/app') ? target : '/app');
  };

  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin + '/callback',
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        scope: 'openid profile email',
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <AuthProviderInner>{children}</AuthProviderInner>
    </Auth0Provider>
  );
}

/** O contexto de autenticação, ou ``null`` fora do provider (componentes reusados em testes isolados). */
export function useAuthOpcional() {
  return useContext(AuthContext);
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext deve ser usado dentro de AuthProvider');
  }
  return context;
}
